import {
  useState,
  useEffect,
  useMemo,
  SetStateAction,
  useCallback,
} from "react";
import useApi, { UseApiProps } from "./useApi";
import { ListResponseData } from "@/utilities/types/requests";
import { DEFAULT_LIMIT } from "@/config/pagination";
import { useUpdateEffect } from "react-use";
import { isEmpty } from "lodash";
import { useDebounce } from "ahooks";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import { removeFalsyValueNestedObject } from "@/utils/form";
interface AnyObject {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}
export type DefaultSortKey = "createdAt" | "updatedAt";
export interface SortConfig<K extends string> {
  key: K | DefaultSortKey;
  direction: "asc" | "desc";
}

export interface UseTableData<T> {
  server: UseApiProps<ListResponseData<T>>;
  initialFilterState?: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  searchBy?: (searchTerm: string) => AnyObject;
  pushSearchParams?: boolean;
  pagination?: {
    limit?: number;
    page?: number;
  };
  callBackFilters?: (filters: Record<string, any>) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export function useTableData<T extends AnyObject>({
  server,
  initialFilterState,
  searchBy,
  pushSearchParams = true,
  pagination = {
    limit: DEFAULT_LIMIT,
    page: 1,
  },
  callBackFilters,
}: UseTableData<T>) {
  const { request, response, loading } = useApi<ListResponseData<T>>({
    ...server,
    reValidate: false,
  });

  // Helper function to check if filter value is valid
  const isValidFilterValue = (value: unknown) => {
    if (value === undefined || value === "") return false;
    if (Array.isArray(value)) {
      // Remove empty strings from array and check if array still has valid values
      const filteredArray = value.filter(
        (item) => item !== "" && item !== undefined && item !== null
      );
      return filteredArray.length > 0;
    }
    return true;
  };

  const location = useLocation();

  const navigate = useNavigate();

  const defaultSearchParams = useMemo(() => {
    if (!pushSearchParams) {
      return {
        limit: pagination?.limit,
        page: pagination?.page,
      };
    }

    const searchParams = qs.parse(location?.search.slice(1));

    if (!searchParams?.limit || !searchParams.page) {
      if (pagination?.limit || pagination?.page) {
        return {
          ...searchParams,
          limit: pagination?.limit,
          page: pagination?.page,
        };
      }

      return null;
    }

    return {
      ...searchParams,
      limit: +searchParams.limit,
      page: +searchParams.page,
    };
  }, [pushSearchParams, location.search, pagination]);

  /*
   * Handle row selection
   */
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const handleRowSelect = (recordKey: string) => {
    const selectedKeys = [...selectedRowKeys];
    if (selectedKeys.includes(recordKey)) {
      setSelectedRowKeys(selectedKeys.filter((key) => key !== recordKey));
    } else {
      setSelectedRowKeys([...selectedKeys, recordKey]);
    }
  };

  const handleSelectAll = () => {
    if (selectedRowKeys.length === pageSize) {
      setSelectedRowKeys([]);
    } else {
      setSelectedRowKeys(
        response?.data?.items?.map((record) => record.id) || []
      );
    }
  };

  /*
   * Handle sorting
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sortConfig, setSortConfig] = useState<SortConfig<any>>({
    key: "createdAt",
    direction: "desc",
  });

  function handleSort(key: string) {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  }

  /*
   * Handle pagination
   */
  const [currentPage, setCurrentPage] = useState(
    defaultSearchParams?.page ?? 1
  );
  const [pageSize, setPageSize] = useState(
    defaultSearchParams?.limit ?? DEFAULT_LIMIT
  );

  const pushSearchParamsUrl = useCallback(
    (page: number, limit: number, search?: string) => {
      if (!pushSearchParams) return;
      const searchParams = qs.parse(location?.search.slice(1));

      const query = qs.stringify(
        {
          ...searchParams,
          search,
          page,
          limit,
        },
        { encode: false }
      );
      navigate(
        {
          pathname: location.pathname,
          search: query,
        },
        {
          state: location?.state,
          replace: true,
        }
      );
    },
    [
      pushSearchParams,
      location?.search,
      location.pathname,
      location?.state,
      navigate,
    ]
  );

  async function refresh() {
    const searchQuery = searchBy?.(debounceSearch.trim());
    const filterQueries = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => isValidFilterValue(value))
    );

    await request({
      queryParams: {
        pagination: {
          limit: pageSize,
          page: currentPage,
        },
        query: {
          search: {
            ...searchQuery,
          },
          filter: filterQueries,
          sort: {
            [sortConfig.key]: sortConfig.direction.toUpperCase(),
          },
        },
      },
    });
  }

  function handlePaginate(pageNumber: number) {
    setCurrentPage(pageNumber);
    const searchQuery = searchBy?.(debounceSearch.trim());
    const filterQueries = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => isValidFilterValue(value))
    );

    request({
      queryParams: {
        pagination: {
          limit: pageSize,
          page: pageNumber,
        },
        query: {
          search: {
            ...searchQuery,
          },
          filter: filterQueries,
          sort: {
            [sortConfig.key]: sortConfig.direction.toUpperCase(),
          },
        },
      },
    });

    pushSearchParamsUrl(pageNumber, pageSize, searchTerm);
  }

  function handleChangePageSize(limit: SetStateAction<number>) {
    setPageSize(limit);
  }

  /*
   * Handle Filters and searching
   */
  const [searchTerm, setSearchTerm] = useState("");

  const debounceSearch = useDebounce(searchTerm, { wait: 500 });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filters, setFilters] = useState<Record<string, any>>(
    initialFilterState || {}
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function updateFilter(columnId: string, filterValue?: string | any[]) {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [columnId]: filterValue,
    }));
  }

  /*
   * Handle searching
   */
  function handleSearch(searchValue: string) {
    setSearchTerm(searchValue);
    pushSearchParamsUrl(currentPage, pageSize, searchValue);
  }

  /*
   * Reset search and filters
   */
  function handleReset() {
    const newFilter = {
      ...initialFilterState,
    };
    setFilters({ ...newFilter });
  }

  /*
   * Set isFiltered and final filtered data
   */

  const isFiltered = !isEmpty(removeFalsyValueNestedObject(filters || {}));

  /*
   * Go to first page when data is filtered and searched
   */
  useUpdateEffect(() => {
    handlePaginate(1);
    callBackFilters?.(filters);
  }, [filters, debounceSearch, pageSize, sortConfig]);

  useEffect(() => {
    if (initialFilterState) setFilters(initialFilterState);
  }, [initialFilterState]);

  useEffect(() => {
    const filterQueries = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => isValidFilterValue(value))
    );

    request({
      queryParams: {
        pagination: {
          limit: pageSize,
          page: currentPage,
        },
        query: {
          sort: {
            [sortConfig.key as string]: sortConfig.direction.toUpperCase(),
          },
          filter: filterQueries,
        },
      },
    });
    pushSearchParamsUrl(currentPage, pageSize, searchTerm);
  }, []); // Empty dependency array - only run once on mount
  // useTable returns
  return {
    isLoading: loading,
    isFiltered,
    tableData: response?.data?.items || [],
    totalPage: response?.data?.totalPage || 0,
    // pagination
    currentPage,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    totalItems: response?.data?.total || 0,
    // sorting
    sortConfig,
    handleSort,
    // row selection
    selectedRowKeys,
    setSelectedRowKeys,
    handleRowSelect,
    handleSelectAll,
    // searching
    searchTerm,
    handleSearch,
    // filters
    filters,
    updateFilter,
    handleReset,
    refresh,
    response,
  };
}
